<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="week-title"
          >
            <b-form-input
              id="week-title"
              v-model="form.title"
              :state="validateState('title')"
              @change="updateAltImage()"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="week-slug"
          >
            <b-form-input
              id="week-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.week')"
            label-for="week-week"
            :state="validateState('week')"
          >
            <b-form-select v-model="form.week">
              <b-form-select-option
                v-for="(week, index) in weekOptions"
                :key="index"
                :value="week"
                :selected="index === 0"
              >{{ week }}</b-form-select-option>
            </b-form-select>
            <b-form-invalid
              id="input-week-feedback"
              :label="$t('general.week')"
              :validator="$v.form.week"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="week-lang"
          >
            <language-selector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.waterMark')"
            label-for="article-watermark"
          >
            <b-form-checkbox
              id="article-watermark"
              v-model="form.watermark"
              class="mt-1"
              name="article-waterMark"
              switch
              inline
            />
            <b-form-invalid
              id="input-watermark-feedback"
              :label="$t('general.waterMark')"
              :validator="$v.form.watermark"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.writer')"
            label-for="week-writer"
          >
            <v-select-custom
              id="week-writer"
              v-model="form.writer"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'writer' }, form.lang)"
              @search="fetchUsersByRole({role: 'writer', 'search' : $event }, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="week-editor"
          >
            <v-select-custom
              id="week-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event }, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.tags')"
            label-for="week-add-tag"
            class="mb-2"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="week-add-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event, 'title')"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedArticles')"
            label-for="week-related-articles"
            class="mb-2"
          >
            <v-select
              id="week-related-articles"
              v-model="form.relatedArticles"
              dir="rtl"
              multiple
              label="title"
              :options="articleOptions"
              @search="fetchArticles(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedServices')"
            label-for="article-related-services"
          >
            <v-select
              id="article-related-services"
              v-model="form.relatedServices"
              dir="rtl"
              label="title"
              multiple
              :options="serviceOptions"
              @search="fetchServices(form.lang, $event, 'title')"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.serviceThumbnailFileId"
                  :title="option.title"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.serviceThumbnailFileId"
                  :title="option.title"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedQuestions')"
            label-for="article-related-questions"
          >
            <v-select
              id="article-related-questions"
              v-model="form.relatedQuestions"
              dir="rtl"
              label="title"
              multiple
              :options="questionOptions"
              @search="fetchQuestions(form.lang, $event, 'title')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.relatedArticleCategories')"
            label-for="article-related-categories"
          >
            <v-select
              id="article-related-categories"
              v-model="form.relatedCategories"
              dir="rtl"
              label="title"
              multiple
              :options="categoryOptions"
              @search="fetchCategories(form.lang, $event, 'title')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="week-keywords"
            class="mb-2"
          >
            <b-form-input
              id="week-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="week-description"
            class="mb-2"
          >
            <b-form-textarea
              id="week-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="week-fetus"
            class="mb-2"
          >
            <CkeditorBasic
              id="week-fetus"
              v-model="form.fetus"
              :label="$t('general.fetus')"
              :state="validateState('fetus')"
            />
            <b-form-invalid
              id="input-fetus-feedback"
              :label="$t('general.fetus')"
              :validator="$v.form.fetus"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="week-mother"
            class="mb-2"
          >
            <CkeditorBasic
              id="week-mother"
              v-model="form.mother"
              :label="$t('general.mother')"
              :state="validateState('mother')"
            />
            <b-form-invalid
              id="input-mother-feedback"
              :label="$t('general.mother')"
              :validator="$v.form.mother"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="week-activities"
            class="mb-2"
          >
            <CkeditorBasic
              id="week-activities"
              v-model="form.activities"
              :label="$t('general.activities')"
              :state="validateState('activities')"
            />
            <b-form-invalid
              id="input-activities-feedback"
              :label="$t('general.activities')"
              :validator="$v.form.activities"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="week-doNotWorry"
            class="mb-2"
          >
            <CkeditorBasic
              id="week-doNotWorry"
              v-model="form.doNotWorry"
              :label="$t('general.doNotWorry')"
              :state="validateState('doNotWorry')"
            />
            <b-form-invalid
              id="input-doNotWorry-feedback"
              :label="$t('general.doNotWorry')"
              :validator="$v.form.doNotWorry"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="week-musts"
            class="mb-2"
          >
            <CkeditorBasic
              id="week-musts"
              v-model="form.musts"
              :label="$t('general.musts')"
              :state="validateState('musts')"
            />
            <b-form-invalid
              id="input-musts-feedback"
              :label="$t('general.musts')"
              :validator="$v.form.musts"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="week-shouldNot"
            class="mb-2"
          >
            <CkeditorBasic
              id="week-shouldNot"
              v-model="form.shouldNot"
              :label="$t('general.shouldNot')"
              :state="validateState('shouldNot')"
            />
            <b-form-invalid
              id="input-shouldNot-feedback"
              :label="$t('general.shouldNot')"
              :validator="$v.form.shouldNot"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="week-suggestions"
            class="mb-2"
          >
            <CkeditorBasic
              id="week-suggestions"
              v-model="form.suggestions"
              :label="$t('general.suggestions')"
              :state="validateState('suggestions')"
            />
            <b-form-invalid
              id="input-suggestions-feedback"
              :label="$t('general.suggestions')"
              :validator="$v.form.suggestions"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="weekThumbnail"
            v-model="form.weekThumbnail"
            :label="$t('general.thumbnail') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('weekThumbnail')"
            :alt.sync="form.weekThumbnailAlt"
            :alt-validator="$v.form.weekThumbnailAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-week-thumbnail-feedback"
                :label="$t('general.thumbnail')"
                :validator="$v.form.weekThumbnail"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="weekCoverImage"
            v-model="form.weekCoverImage"
            :label="$t('general.cover') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('weekCoverImage')"
            :alt.sync="form.weekCoverImageAlt"
            :alt-validator="$v.form.weekCoverImageAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-week-cover-feedback"
                :label="$t('general.cover')"
                :validator="$v.form.weekCoverImage"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormSelect, BFormSelectOption, BFormTextarea, BRow, BFormCheckbox,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import '@/components/BDataTable/Elements/ImageCell.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import VSelectOption from '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import vSelect from 'vue-select'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import useWeeksList from './useWeeksList'

export default {
  name: 'WeekAdd',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    VSelectOption,
    languageSelector,
    vSelect,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  validations: {
    form: {
      title: {
        required,
        minLength: minLength(3),
      },
      slug: {
        required,
      },
      tags: {
        required,
      },
      week: {
        required,
      },
      fetus: {
        required,
      },
      mother: {
        required,
      },
      activities: {
        required,
      },
      doNotWorry: {
        required,
      },
      musts: {
        required,
      },
      shouldNot: {
        required,
      },
      suggestions: {
        required,
      },
      keywords: {
        required,
        minLength: minLength(3),
      },
      description: {
        required,
        minLength: minLength(10),
      },
      weekThumbnail: {
        required,
        isTrueImage: requiredIf(function () {
          return typeof this.form.weekThumbnail === 'object' && !Object.prototype.hasOwnProperty.call(this.form.weekThumbnail, '_id')
        }),
        isValidImageSize,
      },
      weekCoverImage: {
        required,
        isTrueImage: requiredIf(function () {
          return typeof this.form.weekCoverImage === 'object' && !Object.prototype.hasOwnProperty.call(this.form.weekCoverImage, '_id')
        }),
        isValidImageSize,
      },
      weekThumbnailAlt: {
        required,
        minLength: minLength(3),
      },
      weekCoverImageAlt: {
        required,
        minLength: minLength(3),
      },
    },
  },
  setup() {
    const {
      addItem,
      addNewTag,
      articleOptions,
      serviceOptions,
      questionOptions,
      categoryOptions,
      fetchServices,
      fetchQuestions,
      fetchCategories,
      createTagIdArray,
      errorManager,
      fetchArticles,
      fetchTags,
      fetchUsers,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      itemLoading,
      newTagIds,
      tags,
      userOptions,
      weekOptions,
    } = useWeeksList()
    return {
      addItem,
      addNewTag,
      articleOptions,
      serviceOptions,
      questionOptions,
      categoryOptions,
      fetchServices,
      fetchCategories,
      fetchQuestions,
      createTagIdArray,
      errorManager,
      fetchArticles,
      fetchTags,
      fetchUsers,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      itemLoading,
      newTagIds,
      tags,
      userOptions,
      weekOptions,
    }
  },
  data() {
    return {
      writerOptions: [],
      editorOptions: [],
    }
  },
  watch: {
    'form.title': function (newValue, oldValue) {
      this.$set(this.form, 'slug', this.createSlug(this.form.title))
      if (oldValue === this.form.weekCoverImageAlt) {
        this.$set(this.form, 'weekCoverImageAlt', newValue)
      }
      if (oldValue === this.form.weekThumbnailAlt) {
        this.$set(this.form, 'weekThumbnailAlt', newValue)
      }
    },
  },
  created() {
    this.$set(this.form, 'lang', 'fa')
    this.fetchTags('fa')
    this.fetchUsersByRole({
      role: 'writer',
    }).then(res => {
      this.writerOptions = res
    })
    this.fetchUsersByRole({
      role: 'editor',
    }).then(res => {
      this.editorOptions = res
    })
    this.fetchArticles(this.form.lang)
    this.fetchCategories(this.form.lang)
    this.fetchServices(this.form.lang)
    this.fetchQuestions(this.form.lang)
  },
  methods: {
    updateAltImage() {
      if (!this.form.weekCoverImageAlt) {
        this.$set(this.form, 'weekCoverImageAlt', this.form.title)
      }
      if (!this.form.weekThumbnailAlt) {
        this.$set(this.form, 'weekThumbnailAlt', this.form.title)
      }
    },
    changeSelectedLanguage(selectedLanguage) {
      this.fetchArticles(selectedLanguage)
      this.fetchTags(selectedLanguage)
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['weekCoverImage', 'weekThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        slug: form.slug,
        writerId: form.writer ? form.writer._id : null,
        editorId: form.editor ? form.editor._id : null,
        week: form.week,
        fetus: form.fetus,
        mother: form.mother,
        activities: form.activities,
        doNotWorry: form.doNotWorry,
        shouldNot: form.shouldNot,
        musts: form.musts,
        keywords: form.keywords,
        description: form.description,
        suggestions: form.suggestions,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        relatedArticleIds: form.relatedArticles ? form.relatedArticles.map(item => item._id) : [],
        relatedQuestionIds: form.relatedQuestions ? form.relatedQuestions.map(item => item._id) : [],
        relatedServiceIds: form.relatedServices ? form.relatedServices.map(item => item._id) : [],
        relatedCategoryIds: form.relatedCategories ? form.relatedCategories.map(item => item._id) : [],
        weekCoverImageFileId: form.weekCoverImage ? form.weekCoverImage._id : null,
        weekThumbnailFileId: form.weekThumbnail ? form.weekThumbnail._id : null,
        watermark: this.form.watermark != null,
      }
      this.addItem(data, this.form.lang)
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss"
</style>
